import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

/**
 * Sprint BG-DataSettings 14 — Card 内 QR コード overlay。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/cardDrawer.ts` の QR 描画 path を L2 で踏襲し、
 * v2 では React 子 component として切り出した。
 *
 * 用途:
 *   - `/m/:urlToken` 等の deep link を QR PNG data URL 化し、`<img>` で card 内に重ねる。
 *   - jspdf `addImage` 経由でも同じ data URL を再利用可 (buildCardPdf 側で生成済を渡す予定)。
 *
 * 旧との差分:
 *   - 旧は cardDrawer 内で同期的に QR を生成 (Promise 一括 await) → 本 sprint は component で useEffect 生成
 *   - dark/light は theme.textColor / theme.backgroundColor 連動
 */

export interface QrCodeOverlayProps {
  /** QR encode する URL。空文字 / null なら描画しない。 */
  value: string | null | undefined;
  /** mm 単位の幅/高さ (正方形)。 */
  size_mm: number;
  /** 配置位置 (左上原点 mm)。 */
  x_mm: number;
  y_mm: number;
  /** dark dot 色 (defaults: #000000)。 */
  darkColor?: string;
  /** background 色 (defaults: #ffffff)。 */
  lightColor?: string;
  /** 詳細 layout (test 用)。 */
  testId?: string;
}

/**
 * URL → QR PNG data URL (pure async helper、test 容易性のため export)。
 * 失敗時は null。
 */
export async function generateQrDataUrl(
  value: string,
  options?: { darkColor?: string; lightColor?: string; widthPx?: number },
): Promise<string | null> {
  if (!value) return null;
  try {
    return await QRCode.toDataURL(value, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: options?.widthPx ?? 256,
      color: {
        dark: options?.darkColor ?? '#000000',
        light: options?.lightColor ?? '#ffffff',
      },
    });
  } catch {
    return null;
  }
}

export function QrCodeOverlay({
  value,
  size_mm,
  x_mm,
  y_mm,
  darkColor = '#000000',
  lightColor = '#ffffff',
  testId,
}: QrCodeOverlayProps): JSX.Element | null {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setDataUrl(null);
      return;
    }
    void generateQrDataUrl(value, { darkColor, lightColor }).then((url) => {
      if (!cancelled) setDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [value, darkColor, lightColor]);

  if (!value || !dataUrl) return null;
  return (
    <img
      src={dataUrl}
      alt=""
      data-testid={testId ?? 'card-qr-overlay'}
      style={{
        position: 'absolute',
        left: `${x_mm}mm`,
        top: `${y_mm}mm`,
        width: `${size_mm}mm`,
        height: `${size_mm}mm`,
        pointerEvents: 'none',
      }}
    />
  );
}
